import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { CircularProgress } from '@material-ui/core';
import { Helmet } from 'react-helmet';
import { stringify } from 'query-string';

// CONSTANTS
import { siteUrl } from '@/constants/common';
import { LOCATIONS_APP_PAGE, LOCATIONS_ON_PAGE } from '@/constants/locations';
import { contentTypes } from '@/constants/watchList';
import { appsListStatuses, appsTypesFilter } from '@/constants/app';
import { APP_PAGE } from '@/constants/swimlanes';
import {
  ROUTE_NO_APP_FOUND,
  ROUTE_SHOWS_AND_MOVIES_DISCOVER,
  ROUTE_DISCOVER,
  ROUTE_DISCOVER_APP,
  ROUTE_PARTNER_DISCOVER_APP,
} from '@/constants/routes';

// UTILITY
import { analyticsPageLoaded, analyticsUpdateAppListStatus } from '@/utility/analytics';
import { sendParentMessage, getSearchParams } from '@/utility/common';
import { getOrgSchema } from '@/utility/orgSchemas/orgSchemas';
import { getS3ImageUrl } from '@/utility/content';
import { getLink, routeEnhance, scrollToSection } from '@/utility/routes';

// STORE
import { AppDetailsActions, WPBlogActions } from '@/store/actions';

// HOC
import Layout from '@/hoc/Layout';
import withSplit from '@/hoc/withSplit';
import withRouter from '@/hoc/withRouter';

// COMPONETS
import Insights from '@/components/Insights';

// HELP
import Hero from './help/Hero';
import Packages from './help/Packages';
// import Subscription from './help/Subscription';
import About from './help/About';
import Gallery from './help/Gallery';
// import Addons from './help/Addons';
import Channels from './help/Channels';
import BackToResults from './help/BackToResults';
import RatingsRevies from './help/RatingsRevies';
import OtherApps from './help/OtherApps';
import Deals from './help/Deals';

// STYLES
import { Wrapper, ProgressWrap, StyledIframe } from './styles';

const defaultOGImg = getS3ImageUrl('/og-image/apps.png');

class AppDetails extends Component {
  constructor(props) {
    super(props);
    this.state = {
      filters: {
        package: '',
        addons: [],
      },
    };
    this.params = getSearchParams(document.location.search);
  }

  componentDidMount() {
    const { isIframe } = this.props;
    const { filters } = this.state;
    window.scrollTo(0, 0);
    window.prerendercloudReady = false;

    if (isIframe) {
      sendParentMessage({ goToTop: true });
    }

    const hasPlanId = this.params.has('plan_id');
    if (hasPlanId) {
      this.setFilters({ ...filters, package: this.params.get('plan_id') });
    }

    this.getPageContent();
  }

  componentDidUpdate(prevProps) {
    const {
      router: {
        params: { slug },
        location,
      },
      isAuth,
      data,
      cleanWPRelatedPosts,
      isIframe,
      loading,
    } = this.props;
    const hasPlanId = this.params.has('plan_id');

    if (prevProps.router.location.search !== location.search) {
      this.params = getSearchParams(location.search);
    }

    if (prevProps.router.params.slug !== slug || prevProps.isAuth !== isAuth) {
      cleanWPRelatedPosts();
      this.getPageContent();
      window.scrollTo(0, 0);

      if (isIframe) {
        sendParentMessage({ goToTop: true });
      }
    }

    if (!hasPlanId && prevProps?.data?.allIds !== data?.allIds && data?.allIds) {
      const allIdsKeys = Object.keys(data.allIds);

      if (allIdsKeys.length) {
        let name = 0;

        if (allIdsKeys.length === 1) {
          name = allIdsKeys[0];
        } else {
          name = allIdsKeys.reduce((acc, key) => {
            if (data.allIds[key]?.name && !acc) {
              return key;
            }

            return acc;
          }, 0);
        }

        this.setFilters({ package: name.toString(), addons: [] });
      }
    }

    if (prevProps.loading !== loading && !loading) {
      scrollToSection();
    }
  }

  componentWillUnmount() {
    const { cleanAppDetails, cleanWPRelatedPosts } = this.props;

    cleanAppDetails();
    cleanWPRelatedPosts();
  }

  getPageContent = (opt = {}) => {
    const {
      router: {
        params: { slug },
        navigate,
      },
      reviewsPagination,
      getAppDetails,
      getAppChannels,
      getAppReviews,
    } = this.props;

    const paginationData = {
      page: opt.page || reviewsPagination.current_page,
      per_page: reviewsPagination.per_page,
    };

    function onError(status) {
      if (status === 422) {
        navigate(routeEnhance(ROUTE_NO_APP_FOUND), { replace: true });
      }

      if (status === 322) {
        navigate(routeEnhance(ROUTE_DISCOVER), { replace: true });
      }
    }

    getAppDetails(slug, onError);

    getAppChannels(slug);
    getAppReviews(slug, paginationData);
  };

  setFilters = filters => {
    this.setState({ filters });
  };

  handleChangeHelmetData = () => {
    if (this.lastPath !== window.location.pathname) {
      analyticsPageLoaded();
      this.lastPath = window.location.pathname;

      window.prerendercloudReady = true;
    }
  };

  handleUpdateAppListStatus = (status, isAuth) => {
    const {
      user,
      data,
      appListUpdateStatus,
      appListUpdateAppStatus,
      statusesLoading,
      cleanWPRelatedPosts,
      partner,
    } = this.props;

    if (!statusesLoading) {
      const sendData = {
        apps: [{ app_id: data.app.id, status }],
      };

      appListUpdateStatus(sendData, () => {
        if (isAuth) {
          appListUpdateAppStatus(data.app.id, status);
        } else {
          cleanWPRelatedPosts();
          this.getPageContent({ page: 1 });
        }

        const eventAction =
          status === appsListStatuses.have ? 'Added App To Profile' : 'Saved App For Later';

        analyticsUpdateAppListStatus(eventAction, {
          app_id: data.app.id,
          app_name: data.app.name,
          user_id: user?.id || '',
          location: LOCATIONS_ON_PAGE,
          partner_id: partner?.id || 0,
        });
      });
    }
  };

  handleRemoveAppListStatus = () => {
    const { data, appListRemoveStatus, appListUpdateAppStatus, statusesLoading } = this.props;

    if (!statusesLoading) {
      appListRemoveStatus(data.app.id, () => appListUpdateAppStatus(data.app.id, null));
    }
  };

  getSimShowsUrl = () => {
    const { data } = this.props;
    const appId = data?.app.id;
    let url = '';

    if (appId) {
      const params = {
        appsType: appsTypesFilter[1].id,
        type: contentTypes.show,
        apps: [appId],
      };

      if (data.merged_plans?.length) {
        const planIds = data.merged_plans.map(plan =>
          plan.type === 'content_plan' ? `${appId}_${plan.id}` : appId.toString(),
        );

        params.apps = [...new Set(planIds)];
      }

      const search = stringify(params, {
        arrayFormat: 'comma',
        skipEmptyString: true,
      });

      url = `${ROUTE_SHOWS_AND_MOVIES_DISCOVER}?${search}`;
    }

    return url;
  };

  handleZipcodeSumbit = () => {
    const {
      router: {
        params: { slug },
      },
      getAppChannels,
    } = this.props;

    getAppChannels(slug);
  };

  handleUpdateAppStatus = (data, status) => {
    const { appListUpdateAppStatus } = this.props;

    appListUpdateAppStatus(data.id, status);
  };

  render() {
    const { filters } = this.state;
    const {
      router: {
        params: { slug },
      },
      treatments,
      isReady,
      data,
      channelsData,
      loading,
      channelsDataLoading,
      isIframe,
      isSimpleView,
      isInAppView,
      unique_id,
      anonymousId,
      relatedPosts,
      isAuth,
      statusesLoading,
      user,
      section1,
      section1Loadings,
      partner,
      partnerLoading,
      virtualCard,
    } = this.props;
    const isPartner = !!partner.slug;
    const hasPlans = Boolean(data && (data.plans?.length || data.merged_plans?.length));
    const hasPartner = !!Object.keys(partner).length;
    const hasHideExternal = this.params.has('hideexternal');

    const userSubscription = data?.user_subscription;
    const appSlug = data?.app.unique_slug;

    const contentLoading = !data || loading || !isReady;

    const seoData = {
      canonicalUrl: `${siteUrl}${getLink(ROUTE_DISCOVER_APP, { slug })}`,
      ogUrl: isPartner
        ? `${siteUrl}${getLink(ROUTE_PARTNER_DISCOVER_APP, { partner: partner.slug, slug })}`
        : `${siteUrl}${getLink(ROUTE_DISCOVER_APP, { slug })}`,
      title: data?.app?.meta_titles || data?.app?.name,
      description:
        data?.app?.meta_description ||
        (data?.app?.super_short_desc?.length > 1
          ? `MyBundle - ${data?.app?.super_short_desc}`
          : `MyBundle - ${data?.app?.short_desc}`),
      img: data?.app?.og_image_url
        ? getS3ImageUrl(`/og-image/apps/${data.app.og_image_url}`)
        : defaultOGImg,
    };

    return (
      <Layout className="app-details-layout">
        <Wrapper className={`app-details-page app-${data?.app?.unique_slug}`}>
          {contentLoading ? (
            <ProgressWrap>
              <CircularProgress />
            </ProgressWrap>
          ) : (
            <>
              {!partnerLoading && (
                <Helmet
                  onChangeClientState={this.handleChangeHelmetData}
                  script={[getOrgSchema('app', data)]}
                  titleTemplate="%s | MyBundle"
                >
                  <title>{seoData.title}</title>
                  <meta property="og:title" content={`${data.app.name} | MyBundle`} />
                  <meta property="og:site_name" content="MyBundle" />
                  <link rel="canonical" href={seoData.canonicalUrl} />
                  <meta property="og:url" content={seoData.ogUrl} />
                  <meta property="og:description" content={seoData.description} />
                  <meta name="description" content={seoData.description} />
                  {data.app.meta_titles && <meta property="title" content={data.app.meta_titles} />}
                  <meta property="og:type" content="website" />
                  <meta property="fb:app_id" content="608514030003288" />
                  <meta property="og:image" content={seoData.img} />
                  <meta name="twitter:image" content={seoData.img} />
                  <meta name="robots" content="index, follow, max-image-preview:large" />
                  <meta name="twitter:title" content={`${data.app.name} | MyBundle`} />
                  <meta name="twitter:description" content={seoData.description} />
                  <meta name="twitter:site" content="@mybundletv" />
                  <meta name="twitter:creator" content="@mybundletv" />
                  <meta name="twitter:card" content="summary" />
                </Helmet>
              )}
              <Hero
                data={data || {}}
                partner={partner}
                user={user}
                virtualCard={virtualCard}
                unique_id={unique_id}
                segmentUserId={anonymousId}
                isAuth={isAuth}
                updateAppListStatus={this.handleUpdateAppListStatus}
                removeAppListStatus={this.handleRemoveAppListStatus}
                statusesLoading={statusesLoading}
                params={this.params}
                isSimpleView={isSimpleView}
                isIframe={isIframe}
                treatments={treatments}
                loading={loading}
                hideExternal={hasHideExternal}
              />
              {data.app.unique_slug === 'curiositystream' && data.app.has_ads === 1 && (
                <>
                  <StyledIframe
                    className="theiframe"
                    title="moneyback"
                    id="myIframe"
                    src="https://rebundle.tv/cs/"
                  />
                  <div />
                </>
              )}
              {hasPlans && (
                <Packages
                  data={data}
                  packages={data.plans || []}
                  plans={data.merged_plans || []}
                  unique_id={unique_id}
                  setFilters={this.setFilters}
                  appName={data.app.name}
                  params={this.params}
                  segmentUserId={anonymousId}
                  partner={partner}
                  activePlanId={userSubscription?.product?.plan_id}
                  appSlug={appSlug}
                  hideExternal={hasHideExternal}
                />
              )}
              {!isInAppView && !hasHideExternal && (
                <OtherApps
                  data={section1}
                  loadings={section1Loadings}
                  analyticsLocation={LOCATIONS_APP_PAGE}
                  onUpdateStatus={this.handleUpdateAppStatus}
                />
              )}
              <About data={data} partner={partner} isIframe={isIframe} unique_id={unique_id} />
              {data.app.app_media && !!data.app.app_media.length ? (
                <Gallery data={data.app.app_media} />
              ) : null}
              {/* {data.addons && !!(data.addons.length) ? (
              <Addons
                addons={data.addons}
                setFilters={this.setFilters}
              />
            ) : null} */}
              {!!channelsData?.channels?.length && (
                <Channels
                  appSlug={data.app.unique_slug}
                  channels={channelsData.channels}
                  loading={channelsDataLoading}
                  filters={filters}
                  packages={data.plans}
                  addons={data.addons || []}
                  allIds={data.allIds}
                  handleZipcodeSumbit={this.handleZipcodeSumbit}
                />
              )}
              {data.app.app_deals && !!data.app.app_deals.length && !hasHideExternal ? (
                <Deals
                  deals={data.app.app_deals}
                  app={data.app}
                  analyticsUserId={unique_id || anonymousId}
                />
              ) : null}
              {data.app && (
                <RatingsRevies
                  appName={data.app?.name}
                  logo={data.app?.thumbnail}
                  appId={data.app?.id}
                  appRating={data.ratings?.rating}
                  ratingCount={data.ratings?.ratingCount}
                  partner={partner}
                />
              )}
              {!!relatedPosts.length && !hasPartner && !hasHideExternal && (
                <div>
                  <Insights relatedPosts={relatedPosts} analyticsLocation={LOCATIONS_APP_PAGE} />
                </div>
              )}
              {isIframe && !hasHideExternal && (
                <BackToResults
                  data={data}
                  partner={partner}
                  analyticsUserId={unique_id || anonymousId}
                  unique_id={unique_id}
                />
              )}
            </>
          )}
        </Wrapper>
      </Layout>
    );
  }
}

AppDetails.propTypes = {
  user: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])]),
  router: PropTypes.object.isRequired,
  treatments: PropTypes.object.isRequired,
  isReady: PropTypes.bool.isRequired,
  data: PropTypes.object,
  channelsData: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  channelsDataLoading: PropTypes.bool.isRequired,
  getAppDetails: PropTypes.func.isRequired,
  getAppChannels: PropTypes.func.isRequired,
  getAppReviews: PropTypes.func.isRequired,
  cleanAppDetails: PropTypes.func.isRequired,
  reviewsPagination: PropTypes.object.isRequired,
  isIframe: PropTypes.bool.isRequired,
  isSimpleView: PropTypes.bool.isRequired,
  isInAppView: PropTypes.bool.isRequired,
  unique_id: PropTypes.string.isRequired,
  isAuth: PropTypes.bool.isRequired,
  anonymousId: PropTypes.string.isRequired,
  relatedPosts: PropTypes.array.isRequired,
  cleanWPRelatedPosts: PropTypes.func.isRequired,
  appListUpdateStatus: PropTypes.func.isRequired,
  appListUpdateAppStatus: PropTypes.func.isRequired,
  appListRemoveStatus: PropTypes.func.isRequired,
  statusesLoading: PropTypes.bool.isRequired,
  section1: PropTypes.object,
  section1Loadings: PropTypes.object.isRequired,
  partner: PropTypes.object.isRequired,
  partnerLoading: PropTypes.bool.isRequired,
  virtualCard: PropTypes.object,
};

const mapStateToProps = ({ auth, appDetails, app, wpBlog, swimlanes, partner, user }) => ({
  user: auth.user,
  unique_id: auth.unique_id,
  isAuth: auth.isAuth,
  anonymousId: auth.anonymousId,
  isIframe: app.isIframe,
  isSimpleView: app.isSimpleView,
  isInAppView: app.isInAppView,
  data: appDetails.data,
  channelsData: appDetails.channelsData,
  reviewsPagination: appDetails.reviews.meta || {},
  loading: appDetails.loadings.data,
  channelsDataLoading: appDetails.loadings.channelsData,
  statusesLoading: appDetails.loadings.statusesLoading,
  relatedPosts: wpBlog.relatedPosts,
  section1: swimlanes.section1[APP_PAGE] || {},
  section1Loadings: swimlanes.loadings.section1[APP_PAGE] || {},
  partner: partner.data,
  partnerLoading: partner.loadings.partner,
  virtualCard: user.virtualCard,
});

const mapDispatchToProps = dispatch => {
  return {
    getAppDetails: (slug, onError) =>
      dispatch(AppDetailsActions.getAppDetails.action({ slug, onError })),
    getAppChannels: slug => dispatch(AppDetailsActions.getAppChannels.action({ slug })),
    getAppReviews: (slug, pagination) => {
      dispatch(AppDetailsActions.getAppReviews.action({ slug, pagination }));
    },
    cleanAppDetails: () => dispatch(AppDetailsActions.cleanAppDetails.action()),
    appListUpdateStatus: (data, callback) => {
      dispatch(AppDetailsActions.appListUpdateStatus.action({ data, callback }));
    },
    appListUpdateAppStatus: (id, status) => {
      dispatch(AppDetailsActions.appListUpdateAppStatus.action({ id, status }));
    },
    appListRemoveStatus: (app_id, callback) => {
      dispatch(AppDetailsActions.appListRemoveStatus.action({ app_id, callback }));
    },
    cleanWPRelatedPosts: () => dispatch(WPBlogActions.cleanWPRelatedPosts.action()),
  };
};

export default compose(
  withSplit(['subscription_btn_name']), // should always be the first!
  connect(mapStateToProps, mapDispatchToProps),
  withRouter,
)(AppDetails);
